import { API_URL } from '@/config';
import { formatDate } from '@/utils/dateFormat';
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Calendar, MapPin, Trash2, Edit, X, Users, CreditCard, CheckCircle, Globe, Lock, FileText, Eye, Vote, Copy, Link, Mail, MessageSquare, Phone, Settings2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/imageUrl';
import { useNavigate } from 'react-router-dom';

const API = `${API_URL}/api`;

const AdminEventsPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [forms, setForms] = useState([]);
  const [channelStatus, setChannelStatus] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmationsDialog, setConfirmationsDialog] = useState(null);
  const [confirmations, setConfirmations] = useState([]);
  const [activeTab, setActiveTab] = useState('basics');
  const [formData, setFormData] = useState({
    event_id: '',
    title: '', 
    description: '', 
    date_time_start: '', 
    date_time_end: '',
    venue: '', 
    banner_image_url: '',
    event_images: [],
    convener_member_id: '', 
    co_convener_member_id: '', 
    chairman_member_id: '',
    confirmation_required: false,
    polling_required: false,
    voting_end_date: '',
    payment_type: 'free',
    registration_amount: '',
    registration_start_date: '',
    registration_end_date: '',
    visibility: 'public',
    registration_form_id: '',
    // Communication channels
    nominee_channels: ['email'],
    voter_channels: ['email'],
    // Terms and Conditions
    terms_and_conditions: '',
    show_terms: false,
    require_consent: false,
    // Custom CTA text
    cta_text: 'Register Now',
    cta_nominate_text: 'Submit Nomination',
    cta_vote_text: 'Vote Now'
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const [eventsRes, membersRes, formsRes, channelRes] = await Promise.all([
        axios.get(`${API}/events`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/members`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/forms`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/settings/channel-status`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
      ]);
      setEvents(eventsRes.data);
      setMembers(membersRes.data);
      setForms(formsRes.data);
      setChannelStatus(channelRes.data);
    } catch (error) {
      toast.error('Failed to load events');
    }
  };

  const resetForm = () => {
    setFormData({
      event_id: '',
      title: '', 
      description: '', 
      date_time_start: '', 
      date_time_end: '',
      venue: '', 
      banner_image_url: '',
      event_images: [],
      convener_member_id: '', 
      co_convener_member_id: '', 
      chairman_member_id: '',
      confirmation_required: false,
      payment_type: 'free',
      registration_amount: '',
      registration_start_date: '',
      registration_end_date: '',
      visibility: 'public',
      registration_form_id: '',
      polling_required: false,
      voting_end_date: '',
      // Communication channels
      nominee_channels: ['email'],
      voter_channels: ['email'],
      // Terms and Conditions
      terms_and_conditions: '',
      show_terms: false,
      require_consent: false,
      // Custom CTA text
      cta_text: 'Register Now',
      cta_nominate_text: 'Submit Nomination',
      cta_vote_text: 'Vote Now'
    });
    setEditMode(false);
    setActiveTab('basics');
  };

  const handleFileUpload = async (file, fieldName) => {
    if (!file) return;
    
    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error(`File too large. Maximum size is 10MB. Your file: ${(file.size / (1024 * 1024)).toFixed(1)}MB`);
      return;
    }
    
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem("session_token")}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000, // 60 second timeout for large files
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      });
      
      if (fieldName === 'event_images') {
        setFormData(prev => ({
          ...prev,
          event_images: [...(prev.event_images || []), response.data.url]
        }));
      } else {
        setFormData(prev => ({ ...prev, [fieldName]: response.data.url }));
      }
      
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      if (error.response?.status === 413) {
        toast.error('File too large for server. Try compressing the image.');
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Upload timed out. Try a smaller file.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to upload image');
      }
    } finally {
      setUploading(false);
    }
  };

  const removeEventImage = (index) => {
    setFormData(prev => ({
      ...prev,
      event_images: prev.event_images.filter((_, i) => i !== index)
    }));
  };

  const handleEditEvent = (event) => {
    setFormData({
      event_id: event.event_id,
      title: event.title || '',
      description: event.description || '',
      date_time_start: event.date_time_start?.substring(0, 16) || '',
      date_time_end: event.date_time_end?.substring(0, 16) || '',
      venue: event.venue || '',
      banner_image_url: event.banner_image_url || '',
      event_images: event.event_images || [],
      convener_member_id: event.convener_member_id || '',
      co_convener_member_id: event.co_convener_member_id || '',
      chairman_member_id: event.chairman_member_id || '',
      confirmation_required: event.confirmation_required || false,
      polling_required: event.polling_required || false,
      voting_end_date: event.voting_end_date?.substring(0, 16) || '',
      payment_type: event.payment_type || 'free',
      registration_amount: event.registration_amount || '',
      registration_start_date: event.registration_start_date?.substring(0, 16) || '',
      registration_end_date: event.registration_end_date?.substring(0, 16) || '',
      visibility: event.visibility || 'public',
      registration_form_id: event.registration_form_id || '',
      // Communication channels
      nominee_channels: event.nominee_channels || ['email'],
      voter_channels: event.voter_channels || ['email'],
      // Terms and Conditions
      terms_and_conditions: event.terms_and_conditions || '',
      show_terms: event.show_terms || false,
      require_consent: event.require_consent || false,
      // Custom CTA text
      cta_text: event.cta_text || 'Register Now',
      cta_nominate_text: event.cta_nominate_text || 'Submit Nomination',
      cta_vote_text: event.cta_vote_text || 'Vote Now'
    });
    setEditMode(true);
    setActiveTab('basics');
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Clean up the form data - convert empty strings to null for optional fields
      const cleanedData = {
        ...formData,
        registration_amount: formData.registration_amount === '' ? null : formData.registration_amount,
        registration_form_id: formData.registration_form_id === '' ? null : formData.registration_form_id,
        registration_start_date: formData.registration_start_date === '' ? null : formData.registration_start_date,
        registration_end_date: formData.registration_end_date === '' ? null : formData.registration_end_date,
        voting_end_date: formData.voting_end_date === '' ? null : formData.voting_end_date,
        convener_member_id: formData.convener_member_id === '' ? null : formData.convener_member_id,
        co_convener_member_id: formData.co_convener_member_id === '' ? null : formData.co_convener_member_id,
        chairman_member_id: formData.chairman_member_id === '' ? null : formData.chairman_member_id,
        terms_and_conditions: formData.terms_and_conditions === '' ? null : formData.terms_and_conditions,
      };
      
      if (editMode) {
        await axios.put(`${API}/events/${formData.event_id}`, cleanedData, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
        toast.success('Event updated successfully!');
      } else {
        await axios.post(`${API}/events`, cleanedData, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
        toast.success('Event created successfully!');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Event save error:', error.response?.data || error);
      toast.error(editMode ? 'Failed to update event' : 'Failed to create event');
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API}/events/${eventId}`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      toast.success('Event deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const viewConfirmations = async (event) => {
    try {
      const response = await axios.get(`${API}/events/${event.event_id}/confirmations`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      setConfirmations(response.data.confirmations || []);
      setConfirmationsDialog(event);
    } catch (error) {
      toast.error('Failed to load confirmations');
    }
  };

  const getPaymentBadgeColor = (type) => {
    switch (type) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'partially_paid': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getPaymentLabel = (type) => {
    switch (type) {
      case 'paid': return 'Paid';
      case 'partially_paid': return 'Partially Paid';
      default: return 'Free';
    }
  };

  const copyEventLink = (eventId) => {
    const baseUrl = window.location.origin;
    const eventUrl = `${baseUrl}/event/${eventId}`;
    navigator.clipboard.writeText(eventUrl).then(() => {
      toast.success('Event link copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  // Toggle channel selection
  const toggleChannel = (channelType, channel) => {
    const key = channelType === 'nominee' ? 'nominee_channels' : 'voter_channels';
    const current = formData[key] || [];
    if (current.includes(channel)) {
      // Allow removing all channels
      setFormData(prev => ({
        ...prev,
        [key]: current.filter(c => c !== channel)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [key]: [...current, channel]
      }));
    }
  };

  // Channel badge component
  const ChannelBadge = ({ channel, configured }) => {
    const icons = {
      email: <Mail className="w-3 h-3" />,
      sms: <Phone className="w-3 h-3" />,
      whatsapp: <MessageSquare className="w-3 h-3" />
    };
    return (
      <div className="flex items-center gap-1">
        {icons[channel]}
        <span className="capitalize">{channel}</span>
        {configured !== undefined && (
          <span className={`ml-1 text-xs ${configured ? 'text-green-600' : 'text-red-500'}`}>
            {configured ? '✓' : '✗'}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-events-page">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Events Management</h2>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }} 
          className="bg-primary hover:bg-primary/90"
          data-testid="create-event-btn"
        >
          Create Event
        </Button>
      </div>
      
      {/* Create/Edit Event Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Edit Event' : 'Create New Event'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-4 mb-4">
                <TabsTrigger value="basics">Basics</TabsTrigger>
                <TabsTrigger value="communication">Communication</TabsTrigger>
                <TabsTrigger value="terms">Terms & CTA</TabsTrigger>
                <TabsTrigger value="advanced">Advanced</TabsTrigger>
              </TabsList>

              {/* Basics Tab */}
              <TabsContent value="basics" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Title *</Label>
                    <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required data-testid="event-title-input" />
                  </div>
                  <div className="col-span-2">
                    <Label>Description</Label>
                    <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} data-testid="event-description-input" />
                  </div>
                  <div>
                    <Label>Start Date & Time *</Label>
                    <Input type="datetime-local" value={formData.date_time_start} onChange={(e) => setFormData({...formData, date_time_start: e.target.value})} required data-testid="event-start-input" />
                  </div>
                  <div>
                    <Label>End Date & Time</Label>
                    <Input type="datetime-local" value={formData.date_time_end} onChange={(e) => setFormData({...formData, date_time_end: e.target.value})} data-testid="event-end-input" />
                  </div>
                  <div className="col-span-2">
                    <Label>Venue</Label>
                    <Input value={formData.venue} onChange={(e) => setFormData({...formData, venue: e.target.value})} data-testid="event-venue-input" />
                  </div>

                  {/* Confirmation Required */}
                  <div>
                    <Label>Confirmation Required</Label>
                    <Select 
                      value={formData.confirmation_required ? 'yes' : 'no'} 
                      onValueChange={(v) => setFormData({...formData, confirmation_required: v === 'yes'})}
                    >
                      <SelectTrigger data-testid="confirmation-required-select">
                        <SelectValue placeholder="Select option" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="yes">Yes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Polling/Voting Required */}
                  <div>
                    <Label>Polling/Voting Required</Label>
                    <Select 
                      value={formData.polling_required ? 'yes' : 'no'} 
                      onValueChange={(v) => setFormData({...formData, polling_required: v === 'yes'})}
                    >
                      <SelectTrigger data-testid="polling-required-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="yes">Yes - Enable Voting for Nominees</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Voting End Date */}
                  {formData.polling_required && (
                    <div className="col-span-2">
                      <Label>Voting End Date & Time</Label>
                      <Input 
                        type="datetime-local" 
                        value={formData.voting_end_date} 
                        onChange={(e) => setFormData({...formData, voting_end_date: e.target.value})} 
                        data-testid="voting-end-input" 
                      />
                      <p className="text-xs text-muted-foreground mt-1">When voting closes (leave empty to use registration end date)</p>
                    </div>
                  )}

                  {/* Payment Type */}
                  <div>
                    <Label>Payment Type</Label>
                    <Select 
                      value={formData.payment_type} 
                      onValueChange={(v) => setFormData({...formData, payment_type: v})}
                    >
                      <SelectTrigger data-testid="payment-type-select">
                        <SelectValue placeholder="Select payment type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="partially_paid">Partially Paid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {(formData.payment_type === 'paid' || formData.payment_type === 'partially_paid') && (
                    <div>
                      <Label>Registration Amount (₹)</Label>
                      <Input 
                        type="number" 
                        value={formData.registration_amount} 
                        onChange={(e) => setFormData({...formData, registration_amount: e.target.value})} 
                        placeholder="Enter amount"
                        data-testid="registration-amount-input"
                      />
                    </div>
                  )}

                  <div>
                    <Label>Registration Start</Label>
                    <Input type="datetime-local" value={formData.registration_start_date} onChange={(e) => setFormData({...formData, registration_start_date: e.target.value})} />
                  </div>
                  <div>
                    <Label>Registration End</Label>
                    <Input type="datetime-local" value={formData.registration_end_date} onChange={(e) => setFormData({...formData, registration_end_date: e.target.value})} />
                  </div>

                  <div>
                    <Label>Visibility</Label>
                    <Select 
                      value={formData.visibility} 
                      onValueChange={(v) => setFormData({...formData, visibility: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select visibility" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Public</SelectItem>
                        <SelectItem value="members_only">Members Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Registration Form</Label>
                    <Select 
                      value={formData.registration_form_id || 'none'} 
                      onValueChange={(v) => setFormData({...formData, registration_form_id: v === 'none' ? '' : v})}
                    >
                      <SelectTrigger data-testid="registration-form-select">
                        <SelectValue placeholder="Select a form (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No form</SelectItem>
                        {forms.map(form => (
                          <SelectItem key={form.form_id} value={form.form_id}>{form.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>

              {/* Communication Tab */}
              <TabsContent value="communication" className="space-y-6">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                    <Settings2 className="w-4 h-4" />
                    Communication Channels
                  </h4>
                  <p className="text-sm text-blue-700">
                    Select how you want to communicate with nominees and voters for this event.
                  </p>
                </div>

                {/* Nominee Channels */}
                <div className="border rounded-lg p-4">
                  <Label className="text-base font-semibold mb-3 block">Nominee Communication Channels</Label>
                  <p className="text-sm text-muted-foreground mb-4">How to notify registered nominees (confirmation, voting links, updates)</p>
                  <div className="flex flex-wrap gap-4">
                    {['email', 'sms', 'whatsapp'].map(channel => (
                      <div key={channel} className="flex items-center space-x-2">
                        <Checkbox
                          id={`nominee-${channel}`}
                          checked={formData.nominee_channels?.includes(channel)}
                          onCheckedChange={() => toggleChannel('nominee', channel)}
                          disabled={!channelStatus[channel]?.configured && channel !== 'email'}
                        />
                        <label 
                          htmlFor={`nominee-${channel}`}
                          className={`flex items-center gap-2 text-sm cursor-pointer ${!channelStatus[channel]?.configured && channel !== 'email' ? 'text-muted-foreground' : ''}`}
                        >
                          <ChannelBadge channel={channel} configured={channelStatus[channel]?.configured} />
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Voter Channels */}
                <div className="border rounded-lg p-4">
                  <Label className="text-base font-semibold mb-3 block">Voter Communication Channels</Label>
                  <p className="text-sm text-muted-foreground mb-4">How to send OTP and vote confirmations to voters</p>
                  <div className="flex flex-wrap gap-4">
                    {['email', 'sms', 'whatsapp'].map(channel => (
                      <div key={channel} className="flex items-center space-x-2">
                        <Checkbox
                          id={`voter-${channel}`}
                          checked={formData.voter_channels?.includes(channel)}
                          onCheckedChange={() => toggleChannel('voter', channel)}
                          disabled={!channelStatus[channel]?.configured && channel !== 'email'}
                        />
                        <label 
                          htmlFor={`voter-${channel}`}
                          className={`flex items-center gap-2 text-sm cursor-pointer ${!channelStatus[channel]?.configured && channel !== 'email' ? 'text-muted-foreground' : ''}`}
                        >
                          <ChannelBadge channel={channel} configured={channelStatus[channel]?.configured} />
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="text-xs text-muted-foreground">
                  ✓ = Channel configured | ✗ = Not configured (configure in Settings → Integrations)
                </p>
              </TabsContent>

              {/* Terms & CTA Tab */}
              <TabsContent value="terms" className="space-y-6">
                {/* Terms and Conditions */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Terms & Conditions</Label>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center space-x-2">
                        <Switch 
                          id="show-terms" 
                          checked={formData.show_terms}
                          onCheckedChange={(checked) => setFormData({...formData, show_terms: checked})}
                        />
                        <label htmlFor="show-terms" className="text-sm">Show on page</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Switch 
                          id="require-consent" 
                          checked={formData.require_consent}
                          onCheckedChange={(checked) => setFormData({...formData, require_consent: checked})}
                        />
                        <label htmlFor="require-consent" className="text-sm">Require consent</label>
                      </div>
                    </div>
                  </div>
                  <Textarea
                    value={formData.terms_and_conditions}
                    onChange={(e) => setFormData({...formData, terms_and_conditions: e.target.value})}
                    placeholder="Enter terms and conditions... (HTML/Rich text supported)"
                    rows={6}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    You can use HTML tags for formatting: &lt;b&gt;bold&lt;/b&gt;, &lt;i&gt;italic&lt;/i&gt;, &lt;ul&gt;&lt;li&gt;list&lt;/li&gt;&lt;/ul&gt;
                  </p>
                </div>

                {/* Custom CTA Text */}
                <div className="border rounded-lg p-4 space-y-4">
                  <Label className="text-base font-semibold block">Custom Call-to-Action Buttons</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm">Registration Button</Label>
                      <Input
                        value={formData.cta_text}
                        onChange={(e) => setFormData({...formData, cta_text: e.target.value})}
                        placeholder="Register Now"
                        maxLength={30}
                      />
                      <p className="text-xs text-muted-foreground mt-1">Max 30 chars</p>
                    </div>
                    <div>
                      <Label className="text-sm">Nomination Button</Label>
                      <Input
                        value={formData.cta_nominate_text}
                        onChange={(e) => setFormData({...formData, cta_nominate_text: e.target.value})}
                        placeholder="Submit Nomination"
                        maxLength={30}
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Vote Button</Label>
                      <Input
                        value={formData.cta_vote_text}
                        onChange={(e) => setFormData({...formData, cta_vote_text: e.target.value})}
                        placeholder="Vote Now"
                        maxLength={30}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <span className="text-sm text-muted-foreground">Preview:</span>
                    <Badge variant="secondary">{formData.cta_text || 'Register Now'}</Badge>
                    <Badge variant="secondary">{formData.cta_nominate_text || 'Submit Nomination'}</Badge>
                    <Badge variant="secondary">{formData.cta_vote_text || 'Vote Now'}</Badge>
                  </div>
                </div>
              </TabsContent>

              {/* Advanced Tab */}
              <TabsContent value="advanced" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Banner Image */}
                  <div className="col-span-2">
                    <Label>Banner Image</Label>
                    <Input type="file" accept="image/*" onChange={(e) => handleFileUpload(e.target.files[0], 'banner_image_url')} disabled={uploading} data-testid="banner-upload" />
                    {formData.banner_image_url && <p className="text-xs text-muted-foreground mt-1">Banner uploaded</p>}
                  </div>

                  {/* Event Images */}
                  <div className="col-span-2">
                    <Label>Event Images (Up to 3 additional images)</Label>
                    <Input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => handleFileUpload(e.target.files[0], 'event_images')} 
                      disabled={uploading || (formData.event_images?.length >= 3)} 
                      data-testid="event-images-upload" 
                    />
                    {formData.event_images?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.event_images.map((img, idx) => (
                          <div key={idx} className="relative">
                            <img src={getImageUrl(img)} alt={`Event ${idx + 1}`} className="w-20 h-20 object-cover rounded" />
                            <button
                              type="button"
                              onClick={() => removeEventImage(idx)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {formData.event_images?.length || 0} of 3 images uploaded
                    </p>
                  </div>

                  {/* Organizers */}
                  <div className="col-span-2">
                    <Label>Convener</Label>
                    <Select value={formData.convener_member_id} onValueChange={(v) => setFormData({...formData, convener_member_id: v})}>
                      <SelectTrigger data-testid="convener-select"><SelectValue placeholder="Select convener" /></SelectTrigger>
                      <SelectContent>{members.map(m => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Co-Convener</Label>
                    <Select value={formData.co_convener_member_id} onValueChange={(v) => setFormData({...formData, co_convener_member_id: v})}>
                      <SelectTrigger data-testid="co-convener-select"><SelectValue placeholder="Select co-convener" /></SelectTrigger>
                      <SelectContent>{members.map(m => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Chairman</Label>
                    <Select value={formData.chairman_member_id} onValueChange={(v) => setFormData({...formData, chairman_member_id: v})}>
                      <SelectTrigger data-testid="chairman-select"><SelectValue placeholder="Select chairman" /></SelectTrigger>
                      <SelectContent>{members.map(m => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <Button 
              type="submit" 
              className="w-full bg-primary hover:bg-primary/90 mt-4" 
              disabled={uploading}
              data-testid="submit-event-btn"
            >
              {uploading ? 'Uploading...' : (editMode ? 'Update Event' : 'Create Event')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmations Dialog */}
      <Dialog open={!!confirmationsDialog} onOpenChange={() => setConfirmationsDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Event Confirmations - {confirmationsDialog?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-center py-2 bg-muted rounded-lg">
              <span className="text-2xl font-bold text-primary">{confirmations.length}</span>
              <span className="text-muted-foreground ml-2">Total Confirmations</span>
            </div>
            {confirmations.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No confirmations yet</p>
            ) : (
              <div className="space-y-2">
                {confirmations.map((conf) => (
                  <div key={conf.confirmation_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium">{conf.member_name}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {formatDate(conf.confirmed_at)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Events Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {events.map((event) => (
          <Card 
            key={event.event_id} 
            className="hover-lift cursor-pointer overflow-hidden" 
            onClick={() => handleEditEvent(event)}
            data-testid={`event-card-${event.event_id}`}
          >
            {event.banner_image_url && (
              <div className="h-48 overflow-hidden bg-gray-100">
                <img 
                  src={getImageUrl(event.banner_image_url)} 
                  alt={event.title} 
                  className="w-full h-full object-cover" 
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-gray-400"><svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                  }}
                />
              </div>
            )}
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#0F172A' }}>{event.title}</h3>
              {event.description && (
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{event.description}</p>
              )}
              
              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-3">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentBadgeColor(event.payment_type)}`}>
                  <CreditCard className="w-3 h-3 inline mr-1" />
                  {getPaymentLabel(event.payment_type)}
                  {event.registration_amount && ` - ₹${event.registration_amount}`}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${event.visibility === 'public' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
                  {event.visibility === 'public' ? <Globe className="w-3 h-3 inline mr-1" /> : <Lock className="w-3 h-3 inline mr-1" />}
                  {event.visibility === 'public' ? 'Public' : 'Members Only'}
                </span>
                {event.confirmation_required && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    <CheckCircle className="w-3 h-3 inline mr-1" />
                    RSVP Required
                  </span>
                )}
                {event.polling_required && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                    <Vote className="w-3 h-3 inline mr-1" />
                    Voting Enabled
                  </span>
                )}
                {event.registration_form_id && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    <FileText className="w-3 h-3 inline mr-1" />
                    Has Form
                  </span>
                )}
              </div>

              <div className="text-sm text-muted-foreground space-y-2 mb-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {event.date_time_start ? formatDate(event.date_time_start) : 'Date TBD'}
                </div>
                {event.venue && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {event.venue}
                  </div>
                )}
              </div>
              {event.event_images?.length > 0 && (
                <div className="flex gap-1 mb-3">
                  {event.event_images.slice(0, 3).map((img, idx) => (
                    <img 
                      key={idx} 
                      src={getImageUrl(img)} 
                      alt={`Event ${idx}`} 
                      className="w-12 h-12 object-cover rounded bg-gray-100" 
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                      }}
                    />
                  ))}
                </div>
              )}
              <div className="flex gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={(e) => { e.stopPropagation(); handleEditEvent(event); }}
                  data-testid={`edit-event-${event.event_id}`}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="text-green-600 border-green-300 hover:bg-green-50"
                  onClick={(e) => { e.stopPropagation(); copyEventLink(event.event_id); }}
                  data-testid={`copy-link-${event.event_id}`}
                >
                  <Link className="w-4 h-4 mr-1" />
                  Copy Link
                </Button>
                {event.confirmation_required && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-purple-600 border-purple-300 hover:bg-purple-50"
                    onClick={(e) => { e.stopPropagation(); viewConfirmations(event); }}
                    data-testid={`view-confirmations-${event.event_id}`}
                  >
                    <Users className="w-4 h-4 mr-1" />
                    Confirmations
                  </Button>
                )}
                {event.registration_form_id && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    onClick={(e) => { e.stopPropagation(); navigate(`/admin/form-responses?event=${event.event_id}`); }}
                    data-testid={`view-registrations-${event.event_id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Registrations
                  </Button>
                )}
                <Button 
                  size="sm" 
                  variant="destructive" 
                  onClick={(e) => { e.stopPropagation(); handleDelete(event.event_id); }}
                  data-testid={`delete-event-${event.event_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminEventsPage;
